import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  AlertCircle, 
  Download, 
  Calendar,
  Users,
  Package,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  ExternalLink,
  AlertTriangle,
  Trash2,
  ArrowRightLeft,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Printer
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AccountsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Orphaned payments state
  const [orphanedPayments, setOrphanedPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [selectedOrphanedPayment, setSelectedOrphanedPayment] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  
  // Sorting state for outstanding bookings
  const [outstandingSortBy, setOutstandingSortBy] = useState('date'); // 'name', 'date', 'amount'
  const [outstandingSortOrder, setOutstandingSortOrder] = useState('asc'); // 'asc', 'desc'
  
  // Sorting state for overdue bookings
  const [overdueSortBy, setOverdueSortBy] = useState('date');
  const [overdueSortOrder, setOverdueSortOrder] = useState('asc');
  
  // Print report state
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printReportType, setPrintReportType] = useState('summary');

  // Helper function to calculate days till/since event
  const getDaysFromEvent = (eventDate) => {
    if (!eventDate) return { days: null, label: '', isPast: false };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const event = new Date(eventDate);
    event.setHours(0, 0, 0, 0);
    
    const diffTime = event.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return { days: 0, label: 'Today', isPast: false };
    } else if (diffDays > 0) {
      return { days: diffDays, label: `${diffDays} day${diffDays === 1 ? '' : 's'} away`, isPast: false };
    } else {
      const absDays = Math.abs(diffDays);
      return { days: absDays, label: `${absDays} day${absDays === 1 ? '' : 's'} ago`, isPast: true };
    }
  };

  // Sorting function for bookings
  const sortBookings = (bookings, sortBy, sortOrder) => {
    if (!bookings) return [];
    
    return [...bookings].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = (a.client_name || '').localeCompare(b.client_name || '');
          break;
        case 'date':
          const dateA = new Date(a.event_date || '1900-01-01');
          const dateB = new Date(b.event_date || '1900-01-01');
          comparison = dateA.getTime() - dateB.getTime();
          break;
        case 'amount':
          comparison = (a.outstanding || 0) - (b.outstanding || 0);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  // Toggle sort order or change sort field
  const handleSort = (field, currentSortBy, currentSortOrder, setSortBy, setSortOrder) => {
    if (currentSortBy === field) {
      setSortOrder(currentSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Sort button component
  const SortButton = ({ field, label, sortBy, sortOrder, onSort }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onSort(field)}
      className={`text-xs px-2 py-1 h-7 ${sortBy === field ? 'bg-white/10 text-white' : 'text-muted-foreground hover:text-white'}`}
      data-testid={`sort-${field}-btn`}
    >
      {label}
      {sortBy === field && (
        sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />
      )}
      {sortBy !== field && <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />}
    </Button>
  );

  const getDateRange = useCallback(() => {
    const today = new Date();
    let start = null;
    let end = today.toISOString().split('T')[0];

    switch (dateRange) {
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = weekStart.toISOString().split('T')[0];
        break;
      case 'this_month':
        start = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
        break;
      case 'this_quarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = `${today.getFullYear()}-${String(quarter * 3 + 1).padStart(2, '0')}-01`;
        break;
      case 'this_year':
        start = `${today.getFullYear()}-01-01`;
        break;
      case 'custom':
        start = customStartDate || null;
        end = customEndDate || end;
        break;
      default:
        start = null;
        end = null;
    }

    return { start, end };
  }, [dateRange, customStartDate, customEndDate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      let url = `${API}/accounts/summary`;
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch accounts data:', error);
      toast.error('Failed to load accounts data');
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  const fetchOrphanedPayments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payments/orphaned`);
      setOrphanedPayments(response.data);
    } catch (error) {
      console.error('Failed to fetch orphaned payments:', error);
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchOrphanedPayments();
    fetchBookings();
  }, [fetchData, fetchOrphanedPayments, fetchBookings]);

  const handleReassignPayment = async () => {
    if (!selectedOrphanedPayment || !selectedBookingId) {
      toast.error('Please select a booking');
      return;
    }
    try {
      await axios.post(`${API}/payments/${selectedOrphanedPayment.id}/reassign`, {
        booking_id: selectedBookingId
      });
      toast.success('Payment reassigned successfully');
      setShowReassignDialog(false);
      setSelectedOrphanedPayment(null);
      setSelectedBookingId('');
      fetchOrphanedPayments();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign payment');
    }
  };

  const handleDeleteOrphanedPayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to permanently delete this orphaned payment?')) return;
    try {
      await axios.delete(`${API}/payments/orphaned/${paymentId}`);
      toast.success('Orphaned payment deleted');
      fetchOrphanedPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  const handleExport = async (exportType) => {
    try {
      const { start, end } = getDateRange();
      let url = `${API}/accounts/export-csv?export_type=${exportType}`;
      if (start) url += `&start_date=${start}`;
      if (end) url += `&end_date=${end}`;

      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `dj-crm-${exportType}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Export downloaded successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handlePrintReport = (reportType) => {
    if (!data) return;
    
    const { start, end } = getDateRange();
    const dateRangeText = dateRange === 'all' 
      ? 'All Time' 
      : dateRange === 'custom' 
        ? `${customStartDate} to ${customEndDate}`
        : dateRange.replace('_', ' ').replace(/^\w/, c => c.toUpperCase());
    
    // Create print window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print reports');
      return;
    }
    
    let reportContent = '';
    const companyName = user?.company_name || 'DJ CRM';
    const printDate = new Date().toLocaleDateString();
    
    // Common styles
    const styles = `
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
        h1 { color: #0891b2; margin-bottom: 5px; }
        h2 { color: #374151; margin-top: 30px; margin-bottom: 15px; border-bottom: 2px solid #0891b2; padding-bottom: 5px; }
        .header { border-bottom: 2px solid #0891b2; padding-bottom: 20px; margin-bottom: 30px; }
        .meta { color: #6b7280; font-size: 14px; }
        .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 30px; }
        .summary-card { background: #f3f4f6; padding: 20px; border-radius: 8px; text-align: center; }
        .summary-card h3 { margin: 0 0 10px 0; color: #6b7280; font-size: 14px; }
        .summary-card .value { font-size: 28px; font-weight: bold; }
        .green { color: #10b981; }
        .amber { color: #f59e0b; }
        .red { color: #ef4444; }
        .blue { color: #0891b2; }
        table { width: 100%; border-collapse: collapse; margin-top: 15px; }
        th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
        th { background: #f3f4f6; font-weight: 600; }
        tr:hover { background: #f9fafb; }
        .text-right { text-align: right; }
        .total-row { font-weight: bold; background: #f3f4f6; }
        @media print { body { padding: 20px; } }
      </style>
    `;
    
    if (reportType === 'summary') {
      reportContent = `
        <!DOCTYPE html>
        <html>
        <head><title>Financial Summary Report - ${companyName}</title>${styles}</head>
        <body>
          <div class="header">
            <h1>${companyName}</h1>
            <p class="meta">Financial Summary Report • ${dateRangeText} • Generated ${printDate}</p>
          </div>
          
          <div class="summary-grid">
            <div class="summary-card">
              <h3>Total Collected</h3>
              <div class="value green">${formatCurrency(data.summary.total_collected)}</div>
            </div>
            <div class="summary-card">
              <h3>Outstanding</h3>
              <div class="value amber">${formatCurrency(data.summary.total_outstanding)}</div>
            </div>
            <div class="summary-card">
              <h3>Total Booked</h3>
              <div class="value blue">${formatCurrency(data.summary.total_booked)}</div>
            </div>
            <div class="summary-card">
              <h3>Overdue</h3>
              <div class="value red">${formatCurrency(data.summary.overdue_amount)}</div>
            </div>
          </div>
          
          <h2>Revenue by Month</h2>
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th class="text-right">Booked</th>
                <th class="text-right">Collected</th>
                <th class="text-right"># Events</th>
              </tr>
            </thead>
            <tbody>
              ${data.revenue_chart_data.map(item => `
                <tr>
                  <td>${item.month}</td>
                  <td class="text-right">${formatCurrency(item.booked)}</td>
                  <td class="text-right">${formatCurrency(item.collected)}</td>
                  <td class="text-right">${item.count}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <h2>Revenue by Package</h2>
          <table>
            <thead>
              <tr>
                <th>Package</th>
                <th class="text-right">Revenue</th>
                <th class="text-right"># Bookings</th>
              </tr>
            </thead>
            <tbody>
              ${(data.revenue_by_package || []).map(pkg => `
                <tr>
                  <td>${pkg.name}</td>
                  <td class="text-right">${formatCurrency(pkg.revenue)}</td>
                  <td class="text-right">${pkg.count}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;
    } else if (reportType === 'outstanding') {
      reportContent = `
        <!DOCTYPE html>
        <html>
        <head><title>Outstanding Balances Report - ${companyName}</title>${styles}</head>
        <body>
          <div class="header">
            <h1>${companyName}</h1>
            <p class="meta">Outstanding Balances Report • ${dateRangeText} • Generated ${printDate}</p>
          </div>
          
          <div class="summary-grid" style="grid-template-columns: repeat(2, 1fr);">
            <div class="summary-card">
              <h3>Total Outstanding</h3>
              <div class="value amber">${formatCurrency(data.summary.total_outstanding)}</div>
            </div>
            <div class="summary-card">
              <h3># Outstanding Bookings</h3>
              <div class="value blue">${data.summary.outstanding_count}</div>
            </div>
          </div>
          
          <h2>Outstanding Balances</h2>
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Event Date</th>
                <th>Event Type</th>
                <th class="text-right">Total</th>
                <th class="text-right">Paid</th>
                <th class="text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              ${(data.outstanding_bookings || []).map(b => `
                <tr>
                  <td>${b.client_name}</td>
                  <td>${b.event_date}</td>
                  <td>${b.event_type || '-'}</td>
                  <td class="text-right">${formatCurrency(b.total_amount || 0)}</td>
                  <td class="text-right">${formatCurrency(b.paid_amount || 0)}</td>
                  <td class="text-right amber">${formatCurrency(b.outstanding || 0)}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="5">Total Outstanding</td>
                <td class="text-right amber">${formatCurrency(data.summary.total_outstanding)}</td>
              </tr>
            </tbody>
          </table>
        </body>
        </html>
      `;
    } else if (reportType === 'overdue') {
      reportContent = `
        <!DOCTYPE html>
        <html>
        <head><title>Overdue Invoices Report - ${companyName}</title>${styles}</head>
        <body>
          <div class="header">
            <h1>${companyName}</h1>
            <p class="meta">Overdue Invoices Report • ${dateRangeText} • Generated ${printDate}</p>
          </div>
          
          <div class="summary-grid" style="grid-template-columns: repeat(2, 1fr);">
            <div class="summary-card">
              <h3>Total Overdue</h3>
              <div class="value red">${formatCurrency(data.summary.overdue_amount)}</div>
            </div>
            <div class="summary-card">
              <h3># Overdue Invoices</h3>
              <div class="value red">${data.summary.overdue_count}</div>
            </div>
          </div>
          
          <h2>Overdue Invoices</h2>
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Event Date</th>
                <th>Event Type</th>
                <th class="text-right">Amount Due</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${(data.overdue_invoices || []).map(b => `
                <tr>
                  <td>${b.client_name}</td>
                  <td>${b.event_date}</td>
                  <td>${b.event_type || '-'}</td>
                  <td class="text-right red">${formatCurrency(b.outstanding || 0)}</td>
                  <td>Overdue</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="3">Total Overdue</td>
                <td class="text-right red">${formatCurrency(data.summary.overdue_amount)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </body>
        </html>
      `;
    } else if (reportType === 'transactions') {
      reportContent = `
        <!DOCTYPE html>
        <html>
        <head><title>Transactions Report - ${companyName}</title>${styles}</head>
        <body>
          <div class="header">
            <h1>${companyName}</h1>
            <p class="meta">Transactions Report • ${dateRangeText} • Generated ${printDate}</p>
          </div>
          
          <div class="summary-grid" style="grid-template-columns: repeat(2, 1fr);">
            <div class="summary-card">
              <h3>Total Transactions</h3>
              <div class="value blue">${(data.transactions || []).length}</div>
            </div>
            <div class="summary-card">
              <h3>Total Amount</h3>
              <div class="value green">${formatCurrency((data.transactions || []).reduce((sum, t) => sum + (t.amount || 0), 0))}</div>
            </div>
          </div>
          
          <h2>Transaction History</h2>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Client</th>
                <th>Description</th>
                <th>Method</th>
                <th class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${(data.transactions || []).map(t => `
                <tr>
                  <td>${t.date}</td>
                  <td>${t.client_name}</td>
                  <td>${t.description || '-'}</td>
                  <td>${t.method || '-'}</td>
                  <td class="text-right green">${formatCurrency(t.amount || 0)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;
    }
    
    printWindow.document.write(reportContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
    }, 250);
    
    setShowPrintDialog(false);
  };

  return (
    <div className="flex min-h-screen bg-obsidian" data-testid="accounts-page">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />

      <div className="flex-1 p-8 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold mb-2" data-testid="accounts-heading">Accounting</h1>
            <p className="text-muted-foreground">Financial overview and reporting</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Date Range Selector */}
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-40 bg-white/5 border-glass-border" data-testid="date-range-select">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="this_week">This Week</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="this_quarter">This Quarter</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {dateRange === 'custom' && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="bg-white/5 border-glass-border"
                  data-testid="custom-start-date"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="bg-white/5 border-glass-border"
                  data-testid="custom-end-date"
                />
              </div>
            )}

            {/* Export Dropdown */}
            <Select onValueChange={handleExport}>
              <SelectTrigger className="w-40 bg-neon-blue text-black border-none" data-testid="export-select">
                <Download className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Export CSV" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="summary">Summary Report</SelectItem>
                <SelectItem value="transactions">Transactions</SelectItem>
                <SelectItem value="clients">Client Report</SelectItem>
                <SelectItem value="outstanding">Outstanding Balances</SelectItem>
                <SelectItem value="overdue">Overdue Invoices</SelectItem>
              </SelectContent>
            </Select>

            {/* Print Report Button */}
            <Button 
              variant="outline" 
              onClick={() => setShowPrintDialog(true)}
              className="border-glass-border hover:bg-white/10"
              data-testid="print-report-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-neon-blue text-xl">Loading...</div>
          </div>
        ) : data ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="glass-card border-glass-border" data-testid="total-collected-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Collected</p>
                      <p className="text-3xl font-bold text-green-500">
                        {formatCurrency(data.summary.total_collected)}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-green-500/20">
                      <TrendingUp className="w-6 h-6 text-green-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-glass-border" data-testid="total-outstanding-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Outstanding</p>
                      <p className="text-3xl font-bold text-amber-500">
                        {formatCurrency(data.summary.total_outstanding)}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-amber-500/20">
                      <DollarSign className="w-6 h-6 text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-glass-border" data-testid="total-booked-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Booked</p>
                      <p className="text-3xl font-bold text-neon-blue">
                        {formatCurrency(data.summary.total_booked)}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-neon-blue/20">
                      <Calendar className="w-6 h-6 text-neon-blue" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-glass-border" data-testid="overdue-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Overdue</p>
                      <p className="text-3xl font-bold text-red-500">
                        {formatCurrency(data.summary.overdue_amount)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {data.summary.overdue_count} invoice(s)
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-red-500/20">
                      <AlertCircle className="w-6 h-6 text-red-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs for different views */}
            <Tabs defaultValue="overview" className="space-y-6" data-testid="accounts-tabs">
              <TabsList className="glass-card border-glass-border">
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
                <TabsTrigger value="clients" data-testid="tab-clients">Top Clients</TabsTrigger>
                <TabsTrigger value="outstanding" data-testid="tab-outstanding">
                  All Outstanding
                  {data.summary.outstanding_count > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-500">
                      {data.summary.outstanding_count}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="overdue" data-testid="tab-overdue">
                  Overdue
                  {data.summary.overdue_count > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-500">
                      {data.summary.overdue_count}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="orphaned" data-testid="tab-orphaned">
                  Orphaned
                  {orphanedPayments.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-500">
                      {orphanedPayments.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" data-testid="overview-content">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Revenue by Month Chart */}
                  <Card className="glass-card border-glass-border">
                    <CardHeader>
                      <CardTitle className="font-heading text-xl flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-neon-blue" />
                        Revenue by Month
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {data.revenue_chart_data.length > 0 ? (
                        <div className="space-y-3">
                          {data.revenue_chart_data.slice(-6).map((item) => {
                            const maxBooked = Math.max(...data.revenue_chart_data.map(d => d.booked));
                            const percentage = maxBooked > 0 ? (item.booked / maxBooked) * 100 : 0;
                            return (
                              <div key={item.month} className="space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">{item.month}</span>
                                  <span className="font-medium">{formatCurrency(item.booked)}</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-neon-blue to-cyan-400 rounded-full transition-all duration-500"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>Collected: {formatCurrency(item.collected)}</span>
                                  <span>{item.count} booking(s)</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-center text-muted-foreground py-8">No data available</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Revenue by Package */}
                  <Card className="glass-card border-glass-border">
                    <CardHeader>
                      <CardTitle className="font-heading text-xl flex items-center gap-2">
                        <Package className="w-5 h-5 text-neon-blue" />
                        Revenue by Package
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {data.revenue_by_package.length > 0 ? (
                        <div className="space-y-4">
                          {data.revenue_by_package.map((pkg) => (
                            <div key={pkg.package} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                              <div>
                                <p className="font-medium">{pkg.package}</p>
                                <p className="text-xs text-muted-foreground">{pkg.count} booking(s)</p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-neon-blue">{formatCurrency(pkg.booked)}</p>
                                <p className="text-xs text-green-500">
                                  {formatCurrency(pkg.collected)} collected
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-muted-foreground py-8">No data available</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Transactions Tab */}
              <TabsContent value="transactions" data-testid="transactions-content">
                <Card className="glass-card border-glass-border">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading text-xl">Recent Transactions</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport('transactions')}
                      className="border-glass-border hover:bg-white/5"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {data.transactions.length > 0 ? (
                      <div className="space-y-2">
                        {data.transactions.slice(0, 20).map((t) => (
                          <div
                            key={t.id}
                            className="flex items-center justify-between p-4 bg-white/5 rounded-xl"
                            data-testid={`transaction-${t.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`p-2 rounded-lg ${
                                t.type === 'payment' ? 'bg-green-500/20' : 'bg-neon-blue/20'
                              }`}>
                                {t.type === 'payment' ? (
                                  <ArrowDownRight className="w-4 h-4 text-green-500" />
                                ) : (
                                  <FileText className="w-4 h-4 text-neon-blue" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium">{t.description}</p>
                                <p className="text-xs text-muted-foreground">
                                  {t.date} • {t.reference}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-semibold ${
                                t.type === 'payment' ? 'text-green-500' : 'text-foreground'
                              }`}>
                                {t.type === 'payment' ? '+' : ''}{formatCurrency(t.amount)}
                              </p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                t.status === 'completed' || t.status === 'paid'
                                  ? 'bg-green-500/20 text-green-500'
                                  : 'bg-amber-500/20 text-amber-500'
                              }`}>
                                {t.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">No transactions found</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Top Clients Tab */}
              <TabsContent value="clients" data-testid="clients-content">
                <Card className="glass-card border-glass-border">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Users className="w-5 h-5 text-neon-blue" />
                      Top Clients by Revenue
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport('clients')}
                      className="border-glass-border hover:bg-white/5"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {data.top_clients.length > 0 ? (
                      <div className="space-y-3">
                        {data.top_clients.map((client, index) => (
                          <div
                            key={client.email}
                            className="flex items-center justify-between p-4 bg-white/5 rounded-xl"
                            data-testid={`client-${index}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-8 h-8 rounded-full bg-neon-blue/20 flex items-center justify-center text-neon-blue font-bold">
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium">{client.name}</p>
                                <p className="text-xs text-muted-foreground">{client.email}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-neon-blue">{formatCurrency(client.total)}</p>
                              <p className="text-xs text-muted-foreground">
                                {client.bookings} booking(s) • {formatCurrency(client.paid)} paid
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">No client data available</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* All Outstanding Tab */}
              <TabsContent value="outstanding" data-testid="outstanding-content">
                <Card className="glass-card border-glass-border">
                  <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-amber-500" />
                      All Outstanding Balances
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground mr-2">Sort by:</span>
                      <SortButton
                        field="name"
                        label="Name"
                        sortBy={outstandingSortBy}
                        sortOrder={outstandingSortOrder}
                        onSort={(field) => handleSort(field, outstandingSortBy, outstandingSortOrder, setOutstandingSortBy, setOutstandingSortOrder)}
                      />
                      <SortButton
                        field="date"
                        label="Date"
                        sortBy={outstandingSortBy}
                        sortOrder={outstandingSortOrder}
                        onSort={(field) => handleSort(field, outstandingSortBy, outstandingSortOrder, setOutstandingSortBy, setOutstandingSortOrder)}
                      />
                      <SortButton
                        field="amount"
                        label="Amount"
                        sortBy={outstandingSortBy}
                        sortOrder={outstandingSortOrder}
                        onSort={(field) => handleSort(field, outstandingSortBy, outstandingSortOrder, setOutstandingSortBy, setOutstandingSortOrder)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport('outstanding')}
                        className="border-glass-border hover:bg-white/5 ml-2"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {data.outstanding_bookings && data.outstanding_bookings.length > 0 ? (
                      <div className="space-y-3">
                        {sortBookings(data.outstanding_bookings, outstandingSortBy, outstandingSortOrder).map((booking) => {
                          const eventDays = getDaysFromEvent(booking.event_date);
                          return (
                          <div
                            key={booking.id}
                            className="flex items-center justify-between p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl hover:bg-amber-500/10 transition-colors"
                            data-testid={`outstanding-${booking.id}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-medium">{booking.client_name}</p>
                                <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                                  {booking.event_type || 'Event'}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {booking.event_date} • {booking.venue || 'TBD'}
                              </p>
                              <div className="flex items-center gap-3 mt-1">
                                <p className="text-xs text-muted-foreground">
                                  Package: {booking.package || 'N/A'}
                                </p>
                                {eventDays.days !== null && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    eventDays.isPast 
                                      ? 'bg-gray-500/20 text-gray-400' 
                                      : eventDays.days <= 7 
                                        ? 'bg-red-500/20 text-red-400' 
                                        : eventDays.days <= 30 
                                          ? 'bg-amber-500/20 text-amber-400'
                                          : 'bg-green-500/20 text-green-400'
                                  }`}>
                                    {eventDays.label}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-semibold text-amber-500">{formatCurrency(booking.outstanding)}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatCurrency(booking.paid_amount)} of {formatCurrency(booking.total_amount)} paid
                                </p>
                                <div className="w-24 h-1.5 bg-white/10 rounded-full mt-1">
                                  <div 
                                    className="h-full bg-green-500 rounded-full"
                                    style={{ width: `${(booking.paid_amount / booking.total_amount) * 100}%` }}
                                  />
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/bookings?open=${booking.id}&tab=payments`)}
                                className="text-muted-foreground hover:text-foreground"
                                data-testid={`manage-booking-${booking.id}`}
                              >
                                Manage Event
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                          <TrendingUp className="w-8 h-8 text-green-500" />
                        </div>
                        <p className="text-lg font-medium text-green-500">All paid up!</p>
                        <p className="text-muted-foreground">No outstanding balances</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Overdue Tab */}
              <TabsContent value="overdue" data-testid="overdue-content">
                <Card className="glass-card border-glass-border">
                  <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                      Overdue Invoices
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground mr-2">Sort by:</span>
                      <SortButton
                        field="name"
                        label="Name"
                        sortBy={overdueSortBy}
                        sortOrder={overdueSortOrder}
                        onSort={(field) => handleSort(field, overdueSortBy, overdueSortOrder, setOverdueSortBy, setOverdueSortOrder)}
                      />
                      <SortButton
                        field="date"
                        label="Due Date"
                        sortBy={overdueSortBy}
                        sortOrder={overdueSortOrder}
                        onSort={(field) => handleSort(field, overdueSortBy, overdueSortOrder, setOverdueSortBy, setOverdueSortOrder)}
                      />
                      <SortButton
                        field="amount"
                        label="Amount"
                        sortBy={overdueSortBy}
                        sortOrder={overdueSortOrder}
                        onSort={(field) => handleSort(field, overdueSortBy, overdueSortOrder, setOverdueSortBy, setOverdueSortOrder)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport('overdue')}
                        className="border-glass-border hover:bg-white/5 ml-2"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {data.overdue_invoices.length > 0 ? (
                      <div className="space-y-3">
                        {[...data.overdue_invoices].sort((a, b) => {
                          let comparison = 0;
                          switch (overdueSortBy) {
                            case 'name':
                              comparison = (a.client_name || '').localeCompare(b.client_name || '');
                              break;
                            case 'date':
                              const dateA = new Date(a.due_date || '1900-01-01');
                              const dateB = new Date(b.due_date || '1900-01-01');
                              comparison = dateA.getTime() - dateB.getTime();
                              break;
                            case 'amount':
                              comparison = (a.outstanding || 0) - (b.outstanding || 0);
                              break;
                            default:
                              comparison = 0;
                          }
                          return overdueSortOrder === 'asc' ? comparison : -comparison;
                        }).map((inv) => (
                          <div
                            key={inv.id}
                            className="flex items-center justify-between p-4 bg-red-500/5 border border-red-500/20 rounded-xl hover:bg-red-500/10 transition-colors"
                            data-testid={`overdue-${inv.id}`}
                          >
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-medium">{inv.invoice_number}</p>
                                <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-500">
                                  {inv.days_overdue} days overdue
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">{inv.client_name}</p>
                              <p className="text-xs text-muted-foreground">Due: {inv.due_date}</p>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-semibold text-red-500">{formatCurrency(inv.outstanding)}</p>
                                <p className="text-xs text-muted-foreground">
                                  of {formatCurrency(inv.amount)} total
                                </p>
                              </div>
                              {inv.booking_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/bookings?open=${inv.booking_id}&tab=payments`)}
                                  className="text-muted-foreground hover:text-foreground"
                                  data-testid={`manage-overdue-${inv.id}`}
                                >
                                  Manage Event
                                  <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                          <TrendingUp className="w-8 h-8 text-green-500" />
                        </div>
                        <p className="text-lg font-medium text-green-500">All caught up!</p>
                        <p className="text-muted-foreground">No overdue invoices</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Orphaned Payments Tab */}
              <TabsContent value="orphaned" data-testid="orphaned-content">
                <Card className="glass-card border-glass-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      Orphaned Payments
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Payments linked to bookings that no longer exist. You can reassign them to another booking or delete them.
                    </p>
                  </CardHeader>
                  <CardContent>
                    {orphanedPayments.length > 0 ? (
                      <div className="space-y-3">
                        {orphanedPayments.map((payment) => (
                          <div
                            key={payment.id}
                            className="flex items-center justify-between p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30"
                            data-testid={`orphaned-payment-${payment.id}`}
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-yellow-500">
                                  {formatCurrency(payment.amount)}
                                </span>
                                <span className="text-sm text-muted-foreground">
                                  via {payment.payment_method}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Original Booking ID: {payment.booking_id?.substring(0, 8)}...
                              </p>
                              {payment.notes && (
                                <p className="text-xs text-muted-foreground">
                                  Notes: {payment.notes}
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground">
                                Date: {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'Unknown'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedOrphanedPayment(payment);
                                  setSelectedBookingId('');
                                  setShowReassignDialog(true);
                                }}
                                className="border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10"
                                data-testid={`reassign-${payment.id}`}
                              >
                                <ArrowRightLeft className="w-4 h-4 mr-1" />
                                Reassign
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteOrphanedPayment(payment.id)}
                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                data-testid={`delete-orphaned-${payment.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                          <TrendingUp className="w-8 h-8 text-green-500" />
                        </div>
                        <p className="text-lg font-medium text-green-500">All clear!</p>
                        <p className="text-muted-foreground">No orphaned payments</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            No data available
          </div>
        )}
      </div>

      {/* Reassign Payment Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-yellow-500" />
              Reassign Payment
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrphanedPayment && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <p className="font-semibold text-yellow-500">
                  {formatCurrency(selectedOrphanedPayment.amount)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedOrphanedPayment.payment_method} payment
                </p>
              </div>
              
              <div>
                <Label className="text-foreground">Assign to Booking</Label>
                <Select
                  value={selectedBookingId}
                  onValueChange={setSelectedBookingId}
                >
                  <SelectTrigger className="bg-muted border-border mt-1">
                    <SelectValue placeholder="Select a booking..." />
                  </SelectTrigger>
                  <SelectContent>
                    {bookings.map((booking) => (
                      <SelectItem key={booking.id} value={booking.id}>
                        {booking.client_name} - {booking.event_type} ({new Date(booking.event_date).toLocaleDateString()})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  The payment will be added to the selected booking's paid amount.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassignDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReassignPayment}
              disabled={!selectedBookingId}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Reassign Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Report Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="glass-card border-glass-border" data-testid="print-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Printer className="w-5 h-5 text-neon-blue" />
              Print Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-muted-foreground text-sm">
              Select a report type to print. The report will use your current date range filter: 
              <span className="text-white font-medium ml-1">
                {dateRange === 'all' ? 'All Time' : 
                 dateRange === 'custom' ? `${customStartDate} to ${customEndDate}` :
                 dateRange.replace('_', ' ').replace(/^\w/, c => c.toUpperCase())}
              </span>
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 border-glass-border hover:bg-white/10 hover:border-neon-blue"
                onClick={() => handlePrintReport('summary')}
                data-testid="print-summary-btn"
              >
                <TrendingUp className="w-6 h-6 text-neon-blue" />
                <span>Summary Report</span>
                <span className="text-xs text-muted-foreground">Overview & Revenue</span>
              </Button>
              
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 border-glass-border hover:bg-white/10 hover:border-green-500"
                onClick={() => handlePrintReport('transactions')}
                data-testid="print-transactions-btn"
              >
                <FileText className="w-6 h-6 text-green-500" />
                <span>Transactions</span>
                <span className="text-xs text-muted-foreground">Payment History</span>
              </Button>
              
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 border-glass-border hover:bg-white/10 hover:border-amber-500"
                onClick={() => handlePrintReport('outstanding')}
                data-testid="print-outstanding-btn"
              >
                <DollarSign className="w-6 h-6 text-amber-500" />
                <span>Outstanding</span>
                <span className="text-xs text-muted-foreground">Unpaid Balances</span>
              </Button>
              
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 border-glass-border hover:bg-white/10 hover:border-red-500"
                onClick={() => handlePrintReport('overdue')}
                data-testid="print-overdue-btn"
              >
                <AlertCircle className="w-6 h-6 text-red-500" />
                <span>Overdue</span>
                <span className="text-xs text-muted-foreground">Past Due Invoices</span>
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrintDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
